import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';
import useSEO from '../hooks/useSEO';
import RelatedSolutions from '../components/RelatedSolutions';
import CrossSellBlock from '../components/CrossSellBlock';

const { 
  FiZap, FiTarget, FiMessageSquare, FiCheckCircle, FiArrowRight, 
  FiInstagram, FiXCircle, FiShield, FiArrowDown, FiClock, 
  FiUsers, FiCpu, FiTrendingUp, FiActivity, FiUserCheck, 
  FiRepeat, FiMessageCircle, FiChevronDown, FiChevronUp, FiAward, FiStar, FiCalendar
} = FiIcons;

const FitnessOffer = () => {
  const { lang } = useLanguage();
  const [openFaq, setOpenFaq] = useState(null);
  const INSTAGRAM_URL = "https://www.instagram.com/mathiasmazeedeppe/";
  const TIDYCAL_URL = "https://tidycal.com/mathiasdeppe/free-15-minute-growth-review";

  const content = {
    en: {
      seo: {
        title: "Lead Conversion System for Gyms | Mathias Deppe",
        desc: "Turn more Instagram enquiries into paying members with an AI system that qualifies leads and briefs your coaches for the close."
      },
      hero: {
        tag: "ATTENTION BARCELONA GYM OWNERS",
        h1: "Turn More Instagram Enquiries Into Paying Members",
        body: "Your gym is already getting enquiries. The problem is what happens between 'I'm interested' and 'I'm joining.' Your team is busy coaching. They don't have time to sit down with every prospect, answer the same questions, qualify them and follow up endlessly. That's where I come in. I build and manage an AI-powered lead conversion system that handles the first part of the conversation, qualifies the prospect, prepares your coach for the close and follows up when needed.",
        pricingHook: "€75/month + 20% of the first month from every new member generated through the system. So your fixed cost stays low. You pay me more when I help you make more.",
        cta: "Book a Free Growth Review →"
      },
      problem: {
        h2: "YOUR REAL PROBLEM ISN'T GETTING THE LEAD.",
        h2Span: "It's closing the lead.",
        body: "A prospect messages: 'How much is membership?', 'Can I try a class?', 'I've never done CrossFit before.' Your coach is teaching a class. Another prospect walks through the door. Someone else needs help. And suddenly that interested person gets a reply hours later — or never gets followed up. The result? Good leads disappear."
      },
      system: {
        h2: "THE GYM LEAD CONVERSION SYSTEM",
        sub: "We handle the first part. Your coach handles the part that matters most.",
        items: [
          { title: "01 — INSTANT RESPONSE", desc: "Every relevant Instagram enquiry gets a fast response. No more leaving interested prospects waiting." },
          { title: "02 — PRE-QUALIFICATION", desc: "The AI learns what they want, their experience level, their goals, concerns and intent." },
          { title: "03 — COACH CLOSE BRIEF", desc: "Before your coach speaks to the prospect, they get a concise summary of the goal, motivation, concern and buying intent.", highlight: true },
          { title: "04 — BOOKING", desc: "Qualified prospects are guided toward the appropriate trial, intro session or consultation." },
          { title: "05 — SALES COACHING", desc: "Simple sales coaching for your coaches. Practical guidance for handling common objections, asking better closing questions and turning a good trial into a membership conversation." },
          { title: "06 — YOU CLOSE", desc: "Your coach steps in when the prospect is qualified, informed and ready to talk." }
        ]
      },
      resultFlow: {
        h2: "The Shift in Efficiency",
        old: { title: "Instead of:", flow: ["DM", "Coach starts from zero", "Long conversation", "Maybe booking"] },
        new: { title: "You get:", flow: ["DM", "AI Qualification", "Coach Brief", "Trial", "Membership"] },
        subtext: "Your coaches spend less time finding out who the person is. They spend more time helping the right person make a decision."
      },
      bonus: {
        h2: "BONUS: DAILY CONTENT IDEAS",
        body: "Never wonder what to post again. As part of the system, you get practical content ideas based on: questions your prospects keep asking, common objections, your current offers, and your training environment.",
        example: {
          title: "Example:",
          text: "'I'm too unfit to start CrossFit.' -> Record a 20-second clip showing how a beginner movement is scaled.",
          hook: "Hook: Think you're too unfit to start? Watch this.",
          cta: "CTA: DM START."
        },
        footer: "You film it. We give you the idea."
      },
      positioning: {
        h2: "THIS IS NOT A CHATBOT",
        text: "Most chatbots just answer. This system is built around moving a conversation toward a membership.",
        logic: ["Question", "Context", "Qualification", "Coach Brief", "Booking", "Member"]
      },
      teamBenefits: {
        h2: "What Your Team Gets",
        items: [
          { title: "AI Lead Assistant", desc: "24/7 coverage for all incoming DMs and comments." },
          { title: "Lead Qualification", desc: "Automatically filters out tire-kickers and non-locals." },
          { title: "Coach Close Brief", desc: "Strategic summaries for every qualified prospect." },
          { title: "Sales Coaching", desc: "Practical guidance for objections and better closing questions." },
          { title: "Daily Content Ideas", desc: "Strategic hooks based on actual prospect data." },
          { title: "Ongoing Optimisation", desc: "We refine the logic based on your gym's real results." }
        ]
      },
      pricing: {
        h2: "WHAT IT COSTS",
        base: "€75/month",
        baseLabel: "(That's the fixed fee.)",
        plus: "20% commission",
        plusLabel: "of the first month's membership revenue for each new member attributed to the system.",
        example: "A new member joins for €120/month. You pay €24 commission. If the system doesn't generate a new member: No commission.",
        comparison: "Most agencies want €1,000+ setup and €500–€2,000/month. You get: €75/month + performance."
      },
      faq: {
        h2: "Frequently Asked Questions",
        items: [
          { q: "Will the AI replace my coaches?", a: "No. The AI handles the repetitive first part so your coaches can spend time on prospects likely to become members." },
          { q: "Does the AI close the membership?", a: "No. It pre-qualifies, prepares the coach and moves the conversation forward. Your coach owns the final close." },
          { q: "What if I don't get any new members?", a: "You only pay the €75 monthly fee. Commission applies only to attributed members who actually join." },
          { q: "Do I have to create more content?", a: "No. The ideas are a bonus to help you get more leads, but the system works with whatever enquiries you already get." },
          { q: "Do I need ads?", a: "No. The system is designed to maximize the organic enquiries you are already receiving." },
          { q: "What happens if a prospect isn't a good fit?", a: "The system identifies them early and provides a polite automated response, avoiding wasting your team's time." }
        ]
      },
      finalPromise: {
        h2: "THE PROMISE",
        body: "You already have people interested in your gym. I'll help you do more with the conversations you're already getting. €75/month + performance.",
        cta: "Book a Free Growth Review →"
      }
    },
    es: {
      seo: {
        title: "Sistema de Conversión de Leads para Gimnasios | Mathias Deppe",
        desc: "Convierte más consultas de Instagram en socios de pago con un sistema de IA que cualifica leads y prepara a tus coaches para el cierre."
      },
      hero: {
        tag: "ATENCIÓN PROPIETARIOS DE GIMNASIOS EN BARCELONA",
        h1: "Convierte más Consultas de Instagram en Socios de Pago",
        body: "Tu gimnasio ya recibe consultas. El problema es qué pasa entre el 'me interesa' y el 'me apunto'. Tu equipo está ocupado dando clases. No tienen tiempo para sentarse con cada prospecto, responder las mismas dudas, cualificarlos y hacer seguimiento infinito. Ahí es donde entro yo. Construyo y gestiono un sistema de conversión de leads con IA que maneja la primera parte de la charla, cualifica al prospecto, prepara a tu coach para el cierre y hace el seguimiento necesario.",
        pricingHook: "75 €/mes + 20 % del primer mes de cada nuevo socio generado por el sistema. Así tu coste fijo es bajo. Me pagas más cuando te ayudo a ganar más.",
        cta: "Reserva tu Auditoría Gratuita →"
      },
      problem: {
        h2: "TU VERDADERO PROBLEMA NO ES CONSEGUIR EL LEAD.",
        h2Span: "Es cerrar el lead.",
        body: "Un prospecto escribe: '¿Cuánto cuesta la cuota?', '¿Puedo probar una clase?', 'Nunca he hecho CrossFit'. Tu coach está dando clase. Otro prospecto entra por la puerta. Alguien necesita ayuda. Y de repente, esa persona interesada recibe respuesta horas tarde — o nunca se le hace seguimiento. ¿El resultado? Los buenos leads desaparecen."
      },
      system: {
        h2: "EL SISTEMA DE CONVERSIÓN PARA GIMNASIOS",
        sub: "Nosotros manejamos la primera parte. Tu coach maneja la parte que más importa.",
        items: [
          { title: "01 — RESPUESTA INSTANTÁNEA", desc: "Cada consulta relevante en Instagram recibe una respuesta rápida. Se acabó dejar a los interesados esperando." },
          { title: "02 — PRE-CUALIFICACIÓN", desc: "La IA aprende qué quieren, su nivel de experiencia, sus objetivos, preocupaciones e intención." },
          { title: "03 — BRIEFING PARA EL COACH", desc: "Antes de que tu coach hable con el prospecto, recibe un resumen conciso del objetivo, motivación, preocupación e intención de compra.", highlight: true },
          { title: "04 — RESERVA", desc: "Los prospectos cualificados son guiados hacia la prueba, sesión de introducción o consulta adecuada." },
          { title: "05 — COACHING DE VENTAS", desc: "Coaching de ventas sencillo para tus coaches. Guía práctica para manejar objeciones comunes, hacer mejores preguntas de cierre y convertir una buena prueba en una charla de membresía." },
          { title: "06 — TÚ CIERRAS", desc: "Tu coach interviene cuando el prospecto está cualificado, informado y listo para hablar." }
        ]
      },
      resultFlow: {
        h2: "El Cambio en Eficiencia",
        old: { title: "En lugar de:", flow: ["DM", "Coach empieza de cero", "Charla larga", "Quizás reserva"] },
        new: { title: "Obtienes:", flow: ["DM", "Cualificación IA", "Briefing Coach", "Prueba", "Nuevo Socio"] },
        subtext: "Tus coaches pierden menos tiempo averiguando quién es la persona. Pasan más tiempo ayudando a la persona adecuada a decidir."
      },
      bonus: {
        h2: "BONUS: IDEAS DIARIAS DE CONTENIDO",
        body: "No vuelvas a dudar qué publicar. Como parte del sistema, recibes ideas prácticas basadas en: dudas reales de tus prospectos, objeciones comunes y tu entorno de entrenamiento.",
        example: {
          title: "Ejemplo:",
          text: "'No estoy en forma para empezar'. -> Graba un clip de 20 segundos mostrando cómo se escala un movimiento para principiantes.",
          hook: "Gancho: ¿Crees que no estás en forma para empezar? Mira esto.",
          cta: "CTA: DM EMPEZAR."
        },
        footer: "Tú lo grabas. Nosotros te damos la idea."
      },
      positioning: {
        h2: "ESTO NO ES UN CHATBOT",
        text: "La mayoría de los chatbots solo responden. Este sistema está diseñado para mover la charla hacia una membresía.",
        logic: ["Pregunta", "Contexto", "Cualificación", "Briefing Coach", "Reserva", "Socio"]
      },
      teamBenefits: {
        h2: "Lo que recibe tu equipo",
        items: [
          { title: "Asistente de Leads IA", desc: "Cobertura 24/7 para todos los DMs y comentarios entrantes." },
          { title: "Cualificación de Leads", desc: "Filtra automáticamente a los curiosos y a los que no son locales." },
          { title: "Briefing para el Coach", desc: "Resúmenes estratégicos para cada prospecto cualificado." },
          { title: "Coaching de Ventas", desc: "Guía práctica para objeciones y mejores preguntas de cierre." },
          { title: "Ideas de Contenido", desc: "Ganchos estratégicos basados en datos reales de prospectos." },
          { title: "Optimización Continua", desc: "Refinamos la lógica según los resultados reales de tu gimnasio." }
        ]
      },
      pricing: {
        h2: "CUÁNTO CUESTA",
        base: "75 €/mes",
        baseLabel: "(Esa es la cuota fija.)",
        plus: "20 % comisión",
        plusLabel: "del primer mes de membresía por cada nuevo socio atribuido al sistema.",
        example: "Un nuevo socio se apunta por 120 €/mes. Pagas 24 € de comisión. Si el sistema no genera un socio: No hay comisión.",
        comparison: "La mayoría de agencias piden +1.000 € de setup y 500-2.000 €/mes. Tú obtienes: 75 €/mes + resultados."
      },
      faq: {
        h2: "Preguntas Frecuentes",
        items: [
          { q: "¿Sustituirá la IA a mis coaches?", a: "No. La IA maneja la parte repetitiva inicial para que tus coaches dediquen tiempo a prospectos con alta probabilidad de apuntarse." },
          { q: "¿La IA cierra la venta?", a: "No. Pre-cualifica, prepara al coach y hace avanzar la charla. Tu coach es el dueño del cierre final." },
          { q: "¿Qué pasa si no consigo nuevos socios?", a: "Solo pagas la cuota de 75 € mensuales. La comisión solo se aplica a socios atribuidos que realmente se inscriban." },
          { q: "¿Tengo que crear más contenido?", a: "No. Las ideas son un extra para ayudarte, pero el sistema funciona con las consultas que ya recibes." },
          { q: "¿Necesito anuncios?", a: "No. El sistema está diseñado para maximizar las consultas orgánicas que ya te llegan." },
          { q: "¿Qué pasa si un prospecto no encaja?", a: "El sistema lo identifica pronto y da una respuesta educada automática, evitando que tu equipo pierda el tiempo." }
        ]
      },
      finalPromise: {
        h2: "LA PROMESA",
        body: "Ya tienes gente interesada en tu gimnasio. Te ayudaré a sacar más provecho de las charlas que ya estás teniendo. 75 €/mes + resultados.",
        cta: "Reserva tu Auditoría Gratuita →"
      }
    }
  };

  const t = content[lang] || content.en;
  useSEO(t.seo.title, t.seo.desc);

  const relatedLinks = [
    { 
      path: "/ai-lead-agent", 
      label: lang === 'es' ? "Asistente de Leads con IA" : "AI Lead Assistant",
      description: lang === 'es' ? "Cualifica prospectos y gestiona reservas en piloto automático." : "Qualify prospects and manage bookings on autopilot."
    }
  ];

  return (
    <div className="bg-zinc-950 text-white min-h-screen selection:bg-orange-500/30">
      {/* 1. HERO SECTION */}
      <section className="relative pt-40 pb-24 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-orange-500/10 blur-[120px] pointer-events-none" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="inline-block px-4 py-1.5 bg-orange-500/10 border border-orange-500/20 text-orange-500 text-[10px] md:text-xs font-black tracking-[0.2em] uppercase rounded-full mb-8">
              {t.hero.tag}
            </span>
            <h1 className="text-4xl md:text-7xl font-black tracking-tighter leading-[0.9] mb-8 uppercase">
              {t.hero.h1}
            </h1>
            <p className="max-w-3xl mx-auto text-zinc-400 text-lg md:text-xl leading-relaxed mb-12">
              {t.hero.body}
            </p>
            
            <div className="max-w-2xl mx-auto p-6 bg-zinc-900 border border-orange-500/30 rounded-3xl mb-12 shadow-[0_0_50px_rgba(249,115,22,0.1)]">
              <p className="text-orange-500 font-bold text-lg md:text-xl italic">
                {t.hero.pricingHook}
              </p>
            </div>

            <a href={TIDYCAL_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 px-10 py-5 bg-white text-zinc-950 font-black rounded-full hover:bg-orange-500 hover:text-white transition-all transform hover:scale-105 shadow-2xl uppercase tracking-tighter">
              {t.hero.cta}
            </a>
          </motion.div>
        </div>
      </section>

      {/* 2. THE PROBLEM */}
      <section className="py-24 px-6 bg-zinc-900/30">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-black mb-8 tracking-tighter uppercase leading-tight">
            {t.problem.h2}<br />
            <span className="text-orange-500">{t.problem.h2Span}</span>
          </h2>
          <p className="text-xl md:text-2xl text-zinc-400 leading-relaxed italic">
            {t.problem.body}
          </p>
        </div>
      </section>

      {/* 3. THE SYSTEM */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-5xl font-black mb-4 tracking-tighter uppercase">{t.system.h2}</h2>
            <p className="text-xl text-zinc-500 font-medium">{t.system.sub}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {t.system.items.map((item, i) => (
              <motion.div 
                key={i}
                whileInView={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 20 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`p-8 rounded-3xl border transition-all group ${item.highlight ? 'bg-orange-500/10 border-orange-500 shadow-[0_0_30px_rgba(249,115,22,0.1)]' : 'bg-zinc-900 border-white/5 hover:border-orange-500/20'}`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-8 border transition-colors ${item.highlight ? 'bg-orange-500 text-white border-orange-500' : 'bg-zinc-950 border-white/5 group-hover:border-orange-500/50'}`}>
                  <SafeIcon icon={[FiZap, FiTarget, FiAward, FiCalendar, FiShield, FiUserCheck][i]} className="text-xl" />
                </div>
                <h3 className="text-xl font-black mb-4 tracking-tight text-white uppercase">{item.title}</h3>
                <p className={`${item.highlight ? 'text-zinc-300' : 'text-zinc-400'} leading-relaxed text-sm`}>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. THE RESULT (Visual Flow) */}
      <section className="py-24 px-6 bg-zinc-900/20 overflow-hidden border-y border-white/5">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-black text-center mb-20 tracking-tighter uppercase">{t.resultFlow.h2}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Old Flow */}
            <div className="p-8 bg-zinc-950/50 border border-white/5 rounded-[2.5rem] opacity-50 grayscale">
              <h3 className="text-zinc-500 font-black uppercase tracking-widest text-sm mb-8">{t.resultFlow.old.title}</h3>
              <div className="flex flex-col gap-4">
                {t.resultFlow.old.flow.map((step, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-zinc-900 flex items-center justify-center text-xs text-zinc-600 font-black">{i+1}</div>
                    <span className="text-zinc-500 font-bold uppercase tracking-tight text-sm">{step}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* New Flow */}
            <div className="p-10 bg-zinc-900 border-2 border-orange-500 rounded-[3rem] shadow-[0_0_50px_rgba(249,115,22,0.15)] relative">
              <div className="absolute -top-4 -right-4 bg-orange-500 text-white px-4 py-1 rounded-full text-[10px] font-black tracking-widest uppercase">EFFICIENT</div>
              <h3 className="text-orange-500 font-black uppercase tracking-widest text-sm mb-8">{t.resultFlow.new.title}</h3>
              <div className="flex flex-col gap-4">
                {t.resultFlow.new.flow.map((step, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-xs text-white font-black">{i+1}</div>
                    <span className="text-white font-black uppercase tracking-tight text-sm">{step}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <p className="text-center mt-12 text-zinc-500 italic text-lg max-w-2xl mx-auto">
            {t.resultFlow.subtext}
          </p>
        </div>
      </section>

      {/* 5. BONUS: DAILY CONTENT IDEAS */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div 
            whileInView={{ opacity: 1, scale: 1 }}
            initial={{ opacity: 0, scale: 0.95 }}
            viewport={{ once: true }}
            className="p-12 md:p-16 bg-zinc-900 border-2 border-dashed border-orange-500/50 rounded-[3rem] relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-8 opacity-10">
              <SafeIcon icon={FiStar} className="text-8xl text-orange-500" />
            </div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-black mb-8 tracking-tighter uppercase text-white">{t.bonus.h2}</h2>
              <p className="text-xl text-zinc-400 mb-12 max-w-2xl leading-relaxed">
                {t.bonus.body}
              </p>
              
              <div className="p-8 bg-zinc-950 rounded-2xl border border-white/5 text-left mb-12">
                <h4 className="text-orange-500 font-black uppercase tracking-widest text-xs mb-4">{t.bonus.example.title}</h4>
                <p className="text-white font-bold text-lg mb-4">{t.bonus.example.text}</p>
                <div className="space-y-1">
                  <p className="text-zinc-500 text-sm font-medium">{t.bonus.example.hook}</p>
                  <p className="text-zinc-500 text-sm font-medium">{t.bonus.example.cta}</p>
                </div>
              </div>

              <p className="text-orange-500 font-black uppercase tracking-[0.3em] text-sm">
                {t.bonus.footer}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 6. POSITIONING */}
      <section className="py-24 px-6 bg-zinc-900/30">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-black mb-8 tracking-tighter uppercase text-white">{t.positioning.h2}</h2>
          <p className="text-xl md:text-2xl text-white font-bold leading-relaxed mb-12 italic">
            {t.positioning.text}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            {t.positioning.logic.map((step, i) => (
              <React.Fragment key={i}>
                <span className={`px-4 py-2 rounded-lg text-sm font-black uppercase tracking-widest ${i === 3 ? 'bg-orange-500 text-white' : 'bg-zinc-950 border border-white/10 text-orange-500'}`}>
                  {step}
                </span>
                {i < t.positioning.logic.length - 1 && (
                  <SafeIcon icon={FiArrowRight} className="text-zinc-600" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* 7. WHAT YOUR TEAM GETS */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-black text-center mb-16 tracking-tighter uppercase">{t.teamBenefits.h2}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.teamBenefits.items.map((item, i) => (
              <div key={i} className="p-8 bg-zinc-950 border border-white/5 rounded-3xl group hover:border-orange-500/30 transition-all">
                <SafeIcon icon={[FiCpu, FiTarget, FiAward, FiShield, FiStar, FiTrendingUp][i]} className="text-orange-500 text-2xl mb-6" />
                <h3 className="text-xl font-bold mb-4 text-white uppercase tracking-tight">{item.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. WHO THIS IS FOR (Do's and Don'ts) */}
      <section className="py-24 px-6 bg-zinc-900/30">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="p-10 bg-zinc-900 border border-green-500/10 rounded-[2.5rem]">
              <h3 className="text-xl font-black text-green-500 uppercase tracking-widest mb-8 flex items-center gap-3">
                <SafeIcon icon={FiCheckCircle} /> {lang === 'es' ? 'CUÁNDO FUNCIONA MEJOR' : 'WHEN THIS WORKS BEST'}
              </h3>
              <ul className="space-y-6">
                {[
                  lang === 'es' ? "Consultas regulares en Instagram" : "Regular Instagram enquiries",
                  lang === 'es' ? "Equipo de coaches receptivo" : "Responsive coaching team",
                  lang === 'es' ? "Proceso de prueba/intro definido" : "Defined trial/intro process",
                  lang === 'es' ? "Dudas comunes que podemos automatizar" : "Common questions we can train"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-4 text-zinc-300">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2.5 shrink-0" />
                    <span className="font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-10 bg-zinc-900 border border-red-500/10 rounded-[2.5rem] opacity-70 grayscale hover:grayscale-0 transition-all">
              <h3 className="text-xl font-black text-red-500 uppercase tracking-widest mb-8 flex items-center gap-3">
                <SafeIcon icon={FiXCircle} /> {lang === 'es' ? 'NO ES PARA TI SI' : 'NOT A GOOD FIT IF'}
              </h3>
              <ul className="space-y-6">
                {[
                  lang === 'es' ? "Apenas recibes mensajes" : "You barely receive messages",
                  lang === 'es' ? "Tu oferta no está clara" : "Your offer is unclear",
                  lang === 'es' ? "No tienes un siguiente paso definido" : "You don't have a defined next step"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-4 text-zinc-500">
                    <div className="w-1.5 h-1.5 bg-zinc-700 rounded-full mt-2.5 shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 9. PRICING SECTION */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            whileInView={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 40 }}
            viewport={{ once: true }}
            className="bg-zinc-900 border-2 border-orange-500 p-12 md:p-16 rounded-[4rem] text-center shadow-[0_0_100px_rgba(249,115,22,0.1)] relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-8 opacity-10">
              <SafeIcon icon={FiZap} className="text-6xl text-orange-500" />
            </div>
            <h2 className="text-3xl md:text-5xl font-black mb-12 tracking-tighter uppercase text-white">{t.pricing.h2}</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
              <div className="flex flex-col items-center">
                <div className="text-5xl md:text-6xl font-black text-white tracking-tighter mb-2">{t.pricing.base}</div>
                <div className="text-orange-500 font-bold uppercase tracking-widest text-xs">{t.pricing.baseLabel}</div>
              </div>
              <div className="flex flex-col items-center">
                <div className="text-5xl md:text-6xl font-black text-white tracking-tighter mb-2">+{t.pricing.plus}</div>
                <div className="text-orange-500 font-bold uppercase tracking-widest text-xs">{t.pricing.plusLabel}</div>
              </div>
            </div>

            <div className="p-8 bg-zinc-950 rounded-3xl border border-white/5 text-left mb-12">
              <p className="text-zinc-400 text-sm font-medium italic leading-relaxed">
                {t.pricing.example}
              </p>
            </div>
            
            <p className="text-zinc-500 font-bold uppercase tracking-[0.2em] text-xs mb-12">
              {t.pricing.comparison}
            </p>

            <a href={TIDYCAL_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-4 px-12 py-6 bg-white text-zinc-950 font-black text-xl rounded-full hover:bg-orange-500 hover:text-white transition-all transform hover:scale-105 shadow-2xl group uppercase tracking-tighter">
              {t.finalPromise.cta}
              <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-2 transition-transform" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* 10. LIVE DEMO */}
      <section id="demo" className="py-24 px-6 bg-zinc-950 relative overflow-hidden border-t border-white/5">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-orange-500/5 via-transparent to-transparent opacity-50" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-6xl font-black mb-8 tracking-tighter uppercase text-white">
            {lang === 'es' ? 'NO TE FÍES DE MI PALABRA. HABLA CON ÉL.' : "DON'T TAKE MY WORD FOR IT. TALK TO IT."}
          </h2>
          <p className="text-xl md:text-2xl text-zinc-400 mb-12 max-w-3xl mx-auto font-medium leading-relaxed">
            {lang === 'es' ? 'Uso este mismo sistema en mi Instagram. Envía "SISTEMA" por DM y vive la experiencia.' : "I'm using the same system on my own Instagram. Send 'SYSTEM' via DM and experience the speed yourself."}
          </p>
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-4 px-12 py-6 bg-white text-zinc-950 text-xl font-black rounded-full hover:bg-orange-500 hover:text-white transition-all transform hover:scale-105 shadow-2xl group uppercase tracking-tighter">
            {lang === 'es' ? 'PROBAR EL SISTEMA →' : 'TEST THE SYSTEM →'}
            <SafeIcon icon={FiInstagram} className="group-hover:scale-110 transition-transform" />
          </a>
        </div>
      </section>

      {/* 11. FAQ SECTION */}
      <section className="py-24 px-6 bg-zinc-900/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-black text-center mb-16 tracking-tighter uppercase">{t.faq.h2}</h2>
          <div className="space-y-4">
            {t.faq.items.map((item, i) => (
              <div key={i} className="bg-zinc-950 border border-white/5 rounded-2xl overflow-hidden">
                <button 
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-zinc-900/50 transition-colors"
                >
                  <span className="text-lg font-bold text-white pr-8">{item.q}</span>
                  <SafeIcon icon={openFaq === i ? FiChevronUp : FiChevronDown} className="text-orange-500 shrink-0" />
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div 
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="px-8 pb-8 text-zinc-400 leading-relaxed">
                        {item.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-Sell Loop */}
      <CrossSellBlock variant="gym" />

      {/* Internal Linking */}
      <RelatedSolutions links={relatedLinks} />

      {/* 12. FINAL PROMISE & CTA */}
      <section className="py-32 px-6 bg-zinc-950">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div 
            whileInView={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 20 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-8xl font-black text-white mb-8 tracking-tighter uppercase">
              {t.finalPromise.h2}
            </h2>
            <p className="text-xl md:text-2xl text-zinc-400 mb-12 max-w-2xl mx-auto leading-relaxed italic">
              {t.finalPromise.body}
            </p>
            <a href={TIDYCAL_URL} target="_blank" rel="noopener noreferrer" className="px-12 py-6 text-2xl font-black text-zinc-950 bg-white rounded-full hover:bg-orange-500 hover:text-white transition-all duration-300 transform hover:scale-105 shadow-2xl uppercase tracking-tighter">
              {t.finalPromise.cta}
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FitnessOffer;